/// <amd-module name="DistributedTaskUI/SecurityComponent/ProjectPermissionsAuthorization/Store/ProjectAuthorizationStore" />
import * as VSSStore from "VSS/Reflux/Store";
import * as ProjectAuthorizationActionsHub from "ProjectPermissionsAuthorization/Actions/ProjectAuthorizationActionsHub";
import * as ServiceEndpoint from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { TeamProjectReference } from "Tfs/Platform/Contracts/Core";
import { routeUrl } from "VSS/Platform/Location";
import { IVssComponentContext } from "VSS/Platform/Layout";

export interface IProjectPermissionsSecurityGridRow {
    projectImageIconUrl: string;
    projectName: string;
    projectId: string;
    serviceEndpointLink: string;
}

export interface IProjectPermissionsSecurityProjectData {
    projectId: string;
    projectName: string;
    projectImageIconUrl: string;
}

export interface IProjectPermissionsSecurityState {
    projectReferences: IProjectPermissionsSecurityGridRow[];
    allProjects: IProjectPermissionsSecurityProjectData[];
    endpoint: ServiceEndpoint.ServiceEndpoint;
    operation: string;
    error: string;
}

export class ProjectAuthorizationStore extends VSSStore.Store {
    constructor(actions: ProjectAuthorizationActionsHub.ProjectAuthorizationActionsHub, endpoint: ServiceEndpoint.ServiceEndpoint, context: IVssComponentContext, projectId: string) {
        super("ProjectAuthorizationStore");
        this._state = {
            projectReferences: [],
            allProjects: [],
            endpoint: endpoint,
            operation: "",
            error: ""
        };
        this._actions = actions;
        this._endpointId = endpoint.id;
        this._context = context;
        this._projectId = projectId;
        this._projectList = [];
        this.initialize();
    }

    public initialize(): void {
        this._actions.getProjectsData.addListener(this.getProjectsData);
        this._actions.getServiceEndpointDetails.addListener(this.getServiceEndpointDetails);
        this._actions.setErrorMessage.addListener(this.setErrorMessage);
    }

    public dispose(): void {
        this._actions.getProjectsData.removeListener(this.getProjectsData);
        this._actions.getServiceEndpointDetails.removeListener(this.getServiceEndpointDetails);
        this._actions.setErrorMessage.removeListener(this.setErrorMessage);
    }

    public getState(): IProjectPermissionsSecurityState {
        return this._state;
    }

    private getProjectsData = (projects: TeamProjectReference[]): void => {
        let projectData: IProjectPermissionsSecurityGridRow;
        projects.map((project: TeamProjectReference) => {
            projectData = {
                projectImageIconUrl: project.defaultTeamImageUrl,
                projectName: project.name,
                projectId: project.id,
                serviceEndpointLink: this._onInlineLink(project.name)
            };
            this._defaultProjectImageIconUrl = project.defaultTeamImageUrl;
            this._projectDataDictionary[project.id] = projectData;
            this._projectList.push({ projectImageIconUrl: project.defaultTeamImageUrl, projectName: project.name, projectId: project.id });
        });
    }

    private getServiceEndpointDetails = (serviceEndpoint: ServiceEndpoint.ServiceEndpoint): void => {
        this._state.projectReferences = this._getProjectReferences(serviceEndpoint);
        this._state.allProjects = this._getUpdatedProjectList(serviceEndpoint);
        this._state.endpoint = serviceEndpoint;
        this.emitChanged();
    }

    private setErrorMessage = (errorMessage: string): void => {
        this._state.error = errorMessage;
        this.emitChanged();
    }

    private _getProjectReferences = (serviceEndpoint: ServiceEndpoint.ServiceEndpoint): IProjectPermissionsSecurityGridRow[] => {
        let projectReferences: IProjectPermissionsSecurityGridRow[] = [];
        if (serviceEndpoint.serviceEndpointProjectReferences) {
            serviceEndpoint.serviceEndpointProjectReferences.map((serviceEndpointProjectReferences: ServiceEndpoint.ServiceEndpointProjectReference) => {
                if (serviceEndpointProjectReferences.projectReference.id !== this._projectId) {
                    if (this._projectDataDictionary[serviceEndpointProjectReferences.projectReference.id]) {
                        projectReferences.push(this._projectDataDictionary[serviceEndpointProjectReferences.projectReference.id]);
                    }
                    else {
                        projectReferences.push({ projectImageIconUrl: this._defaultProjectImageIconUrl, projectName: serviceEndpointProjectReferences.projectReference.name, projectId: serviceEndpointProjectReferences.projectReference.id, serviceEndpointLink: this._onInlineLink(serviceEndpointProjectReferences.projectReference.name) });
                    }
                }
            });

            projectReferences.sort((a: IProjectPermissionsSecurityGridRow, b: IProjectPermissionsSecurityGridRow) => {
                return a.projectName > b.projectName ? 1 : -1;
            });
        }

        return projectReferences;
    }

    private _getUpdatedProjectList(serviceEndpoint: ServiceEndpoint.ServiceEndpoint): IProjectPermissionsSecurityProjectData[] {
        let projects = this._projectList;
        let isProjectPresent = false;
        if (serviceEndpoint.serviceEndpointProjectReferences && serviceEndpoint.serviceEndpointProjectReferences.length > 0) {
            serviceEndpoint.serviceEndpointProjectReferences.map((serviceEndpointprojectReference: ServiceEndpoint.ServiceEndpointProjectReference) => {
                isProjectPresent = projects.some((project: IProjectPermissionsSecurityProjectData) => project.projectId === serviceEndpointprojectReference.projectReference.id);
                if (isProjectPresent) {
                    projects = projects.filter((project: IProjectPermissionsSecurityProjectData) => project.projectId !== serviceEndpointprojectReference.projectReference.id);
                }
            });
        }
        projects.sort((project1: IProjectPermissionsSecurityProjectData, project2: IProjectPermissionsSecurityProjectData) => {
            return project1.projectName > project2.projectName ? 1 : -1;
        });

        return projects;
    }

    private _onInlineLink = (projectName: string): string => {
        let routeValues: { [key: string]: string } = {
            project: projectName,
            resourceId: this._endpointId
        };
        const summaryUrl = routeUrl(
            this._context.pageContext,
            "ms.vss-distributedtask-web.project-service-endpoint-route",
            routeValues);
        return summaryUrl;
    }

    private _state: IProjectPermissionsSecurityState;
    private _actions: ProjectAuthorizationActionsHub.ProjectAuthorizationActionsHub;
    private _projectDataDictionary: { [key: string]: IProjectPermissionsSecurityGridRow } = {};
    private _endpointId: string;
    private _context: IVssComponentContext;
    private _projectId: string;
    private _projectList: IProjectPermissionsSecurityProjectData[];
    private _defaultProjectImageIconUrl: string;
}
